import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '../../contexts/LanguageContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import ImageUploader from '../../components/ImageUploader';
import { resolveImageUrl } from '../../lib/imageUrl';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminTrainings() {
  const { t, getLocalizedContent } = useLanguage();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    title: { fr: '', en: '' },
    description: { fr: '', en: '' },
    duration: '',
    price: 0,
    start_date: '',
    end_date: '',
    max_participants: 20,
    image_url: '',
    is_active: true
  });

  useEffect(() => { fetchItems(); }, []);

  const fetchItems = async () => {
    try {
      const response = await axios.get(`${API}/trainings?active_only=false`);
      setItems(response.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await axios.put(`${API}/admin/trainings/${editingItem.id}`, formData);
        toast.success('Formation mise à jour');
      } else {
        await axios.post(`${API}/admin/trainings`, formData);
        toast.success('Formation créée');
      }
      setShowModal(false);
      resetForm();
      fetchItems();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ?')) return;
    try {
      await axios.delete(`${API}/admin/trainings/${id}`);
      toast.success('Supprimé');
      fetchItems();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      title: item.title || { fr: '', en: '' },
      description: item.description || { fr: '', en: '' },
      duration: item.duration || '',
      price: item.price || 0,
      start_date: item.start_date || '',
      end_date: item.end_date || '',
      max_participants: item.max_participants || 20,
      image_url: item.image_url || '',
      is_active: item.is_active !== false
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setEditingItem(null);
    setFormData({
      title: { fr: '', en: '' },
      description: { fr: '', en: '' },
      duration: '',
      price: 0,
      start_date: '',
      end_date: '',
      max_participants: 20,
      image_url: '',
      is_active: true
    });
  };

  return (
    <div data-testid="admin-trainings">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-[#0F172A] font-['Montserrat']">{t('admin_trainings')}</h1>
        <Button onClick={() => { resetForm(); setShowModal(true); }} className="bg-[#0B6AB3] hover:bg-[#095c9e] text-white">
          <Plus className="h-4 w-4 mr-2" />{t('admin_new')}
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0B6AB3]"></div></div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((item) => (
            <Card key={item.id} className="border-0 shadow-sm overflow-hidden">
              <div className="h-40">
                <img src={resolveImageUrl(item.image_url) || 'https://via.placeholder.com/400x200'} alt="" className="w-full h-full object-cover" />
              </div>
              <CardContent className="p-4">
                <h3 className="font-semibold text-[#0F172A] mb-1">{getLocalizedContent(item.title)}</h3>
                <p className="text-sm text-gray-500 mb-2">{item.duration} • {item.max_participants} places max</p>
                <p className="text-lg font-bold text-[#0B6AB3]">{item.price?.toLocaleString()} FCFA</p>
                <div className="flex items-center justify-between mt-4">
                  <span className={`px-2 py-1 text-xs rounded-full ${item.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                    {item.is_active ? 'Actif' : 'Inactif'}
                  </span>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => handleEdit(item)}><Edit className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(item.id)} className="text-red-500"><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingItem ? 'Modifier' : 'Nouvelle formation'}</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div><Label>Titre (FR) *</Label><Input value={formData.title.fr} onChange={(e) => setFormData({ ...formData, title: { ...formData.title, fr: e.target.value } })} required /></div>
              <div><Label>Titre (EN)</Label><Input value={formData.title.en} onChange={(e) => setFormData({ ...formData, title: { ...formData.title, en: e.target.value } })} /></div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div><Label>Description (FR) *</Label><Textarea value={formData.description.fr} onChange={(e) => setFormData({ ...formData, description: { ...formData.description, fr: e.target.value } })} rows={3} required /></div>
              <div><Label>Description (EN)</Label><Textarea value={formData.description.en} onChange={(e) => setFormData({ ...formData, description: { ...formData.description, en: e.target.value } })} rows={3} /></div>
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              <div><Label>Durée</Label><Input value={formData.duration} onChange={(e) => setFormData({ ...formData, duration: e.target.value })} placeholder="5 jours" /></div>
              <div><Label>Prix (FCFA)</Label><Input type="number" value={formData.price} onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) || 0 })} /></div>
              <div><Label>Max participants</Label><Input type="number" value={formData.max_participants} onChange={(e) => setFormData({ ...formData, max_participants: parseInt(e.target.value) || 20 })} /></div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div><Label>Date début</Label><Input type="date" value={formData.start_date} onChange={(e) => setFormData({ ...formData, start_date: e.target.value })} /></div>
              <div><Label>Date fin</Label><Input type="date" value={formData.end_date} onChange={(e) => setFormData({ ...formData, end_date: e.target.value })} /></div>
            </div>
            <div>
              <ImageUploader
                label="Image de la formation"
                value={formData.image_url}
                onChange={(url) => setFormData({ ...formData, image_url: url })}
                testIdPrefix="training-image"
              />
            </div>
            <div className="flex items-center gap-2"><Switch checked={formData.is_active} onCheckedChange={(v) => setFormData({ ...formData, is_active: v })} /><Label>Actif</Label></div>
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>{t('common_cancel')}</Button>
              <Button type="submit" className="bg-[#0B6AB3] hover:bg-[#095c9e] text-white">{t('common_save')}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
